import React from 'react';
import { bool, element, node, oneOf, string } from 'prop-types';
import Icon from '../../Icon';
import * as css from './button.css';
import { dt } from '../../../helpers/Utils/GetTailwindToken';

const configButton = {
    length: {
        medium: '40px',
        large: '48px',
    },
    classes: {
        defaults: dt(['buttonDefault', 'font-open-sans-semibold']),
        disabled: dt(['buttonDisabled', 'font-open-sans-semibold']),
        font: {
            medium: 'text-normal',
            large: 'text-large',
        },
        primary: {
            theme: dt(['text-white']),
            enabled: dt(['bg-brand-red', 'hover:bg-error-border', 'focus:bg-error-border']),
            loading: dt(['bg-error-border', 'cursor-not-allowed']),
        },
        primaryAlt: {
            theme: dt(['text-brand-red', 'border', 'border-solid', 'border-brand-red']),
            enabled: dt([
                'bg-white',
                'hover:text-error-border',
                'border',
                'border-solid',
                'border-brand-red',
                'hover:border-error-border',
                'focus:border-error-border',
            ]),
            loading: dt([
                'bg-white',
                'border-1',
                'border-solid',
                'border-error-border',
                'cursor-not-allowed',
            ]),
        },
        secondary: {
            theme: dt(['text-black', 'border', 'border-solid', 'border-gray-40']),
            enabled: dt(['bg-white', 'hover:bg-light-white', 'focus:bg-light-white']),
            loading: dt([
                'bg-light-white',
                'border-1',
                'border-solid',
                'border-gray-40',
                'cursor-not-allowed',
            ]),
        },
        tertiary: {
            theme: dt(['text-white', 'border-solid', 'border-1', 'border-gray-40']),
            enabled: dt(['bg-gray-70', 'hover:bg-black', 'focus:bg-black']),
            loading: dt(['bg-black', 'cursor-not-allowed']),
        },
        text: {
            theme: dt(['textButton']),
            enabled: '',
            loading: '',
        },
    },
};
const configIcon = {
    length: {
        medium: '22px',
        large: '28px',
    },
    classes: 'mr-1',
};
const configSpinner = {
    length: {
        medium: '28px',
        large: '32px',
    },
    classes: {
        defaults: `${css.spinner} relative rounded-full`,
        primary: `${css['spinner-white-100']} ${css['after:bg-error-border']}`,
        primaryAlt: `${css['spinner-brand-red']} ${css['after:bg-white']}`,
        secondary: `${css['spinner-gray-600']} ${css['after:bg-light-white']}`,
        tertiary: `${css['spinner-white-100']} ${css['after:bg-black']}`,
    },
};

const validateState = ({ loading, disabled, href }) => {
    let stateProp;
    let isInvalid = null;

    if (loading) stateProp = 'loading';
    else if (disabled) stateProp = 'disabled';

    if (href && stateProp) {
        isInvalid = new Error(
            ['An `href` cannot be used in combination with `', stateProp, '` in Button.'].join('')
        );
    }

    return isInvalid;
};

/**
 * Button component to standardize the styling and html structure of any button.
 */
const Button = (props) => {
    const {
        automationId,
        children,
        className,
        disabled,
        href,
        icon,
        loading,
        preset,
        size,
        type,
        iconComponent,
        ...attrs
    } = props;

    const commonAttrs = {
        'data-automation-id': automationId,
        style: {
            minHeight: configButton.length[size],
        },
        ...attrs,
    };

    const linkAttrs = {
        href: !disabled ? href : null,
        ...commonAttrs,
    };

    const buttonAttrs = {
        type,
        disabled,
        ...commonAttrs,
    };

    const getClasses = () => {
        const configClasses = configButton.classes;
        const presetConfig = configClasses[preset];

        const classes = [configClasses.defaults, configClasses.font[size]];

        if (className) classes.push(className);

        if (disabled) classes.push(configClasses.disabled);
        else if (loading) classes.push(presetConfig.loading, presetConfig.theme);
        else classes.push(presetConfig.enabled, presetConfig.theme);

        return classes.join(' ');
    };

    const getIcon = () => {
        if (!icon) return null;

        return (
            <Icon
                className={configIcon.classes}
                height={configIcon.length[size]}
                width={configIcon.length[size]}
            >
                {iconComponent}
            </Icon>
        );
    };

    const getSpinner = () => {
        if (!loading) return null;

        const classes = `${configSpinner.classes.defaults} ${configSpinner.classes[preset]}`;

        return (
            <div
                aria-label="Loading..."
                className={classes}
                style={{
                    height: configSpinner.length[size],
                    width: configSpinner.length[size],
                }}
            />
        );
    };

    const getContent = () => (
        <>
            {getSpinner()}
            {getIcon()}
            {children}
        </>
    );

    if (validateState(props, 'href', 'Button')) return null;

    if (href)
        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <a className={getClasses()} {...linkAttrs}>
                {getContent()}
            </a>
        );

    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <button type="button" className={getClasses()} {...buttonAttrs}>
            {getContent()}
        </button>
    );
};

Button.propTypes = {
    /** Automation ID */
    automationId: string,
    /** The text or node(s) to render in the component. */
    children: node,
    /** Additional classes to add to button */
    className: string,
    /** Disables the button when true */
    disabled: bool,
    /** URL to use for the `href` value on an `<a/>` element */
    href: validateState,
    /** Icon to use on button, such as 'ydt-lock' */
    icon: string,
    /** Icon to use on button, such as 'ydt-lock' */
    iconComponent: element,
    /** Displays loading animation when true */
    loading: bool,
    /** Button theme */
    preset: oneOf(['primary', 'secondary', 'tertiary', 'text']),
    /** Button size */
    size: oneOf(['medium', 'large']),
    /** Button `type` attribute */
    type: oneOf(['button', 'submit']),
};

Button.defaultProps = {
    automationId: null,
    children: null,
    className: null,
    disabled: false,
    href: null,
    icon: null,
    loading: false,
    preset: 'primary',
    size: 'medium',
    type: 'button',
    iconComponent: <></>,
};

export default Button;
